import { useState } from 'react';

/**
 * CrimeBackdrop section
 *
 * Renders the all-crime breakdown for transparency, showing every NIBRS
 * code returned by the Crimeometer query. This is informational only —
 * not a scoring driver. The TRIP scoring model uses trafficking-relevant
 * subsets (drugs, violent crime, sex crimes, kidnapping, etc.). This
 * section gives the underwriter the full picture.
 *
 * Expects data prop with shape:
 *   {
 *     available: true,
 *     total: 487,
 *     by_code: [{ code, offense, description, crime_against, count }, ...],
 *     by_crime_against: { Person: 134, Property: 287, Society: 66 },
 *     top_offenses: [...]
 *   }
 */
export default function CrimeBackdrop({ data }) {
  const [showAll, setShowAll] = useState(false);

  if (!data || !data.available || data.total === 0) {
    return (
      <div className="card mb-6">
        <h2 className="section-title">Crime backdrop</h2>
        <div className="text-sm text-gray-400 italic py-2">
          No crime data available for this query.
        </div>
      </div>
    );
  }

  const rows = data.by_code || [];
  const visible = showAll ? rows : rows.slice(0, 8);
  const hiddenCount = rows.length - 8;

  const caBreakdown = data.by_crime_against || {};
  const total = data.total || 0;

  return (
    <div className="card mb-6 animate-fade-in-up" style={{ animationDelay: '0.6s', animationFillMode: 'both' }}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="section-title mb-0">Crime backdrop</h2>
        <div className="text-xs text-gray-500">
          {total} total incidents within 1mi (12mo)
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-4 max-w-3xl">
        All reported incidents in the surrounding area. Shown for context — the TRIP
        score uses trafficking-relevant subsets (drug offenses, violent crime, sex crimes,
        kidnapping). General property crime appears here for transparency but is not a
        direct scoring driver.
      </p>

      {/* Crime-against summary bar */}
      {Object.keys(caBreakdown).length > 0 && (
        <div className="flex gap-4 mb-4 text-xs">
          {Object.entries(caBreakdown).map(([cat, count]) => (
            <CategoryBadge key={cat} category={cat} count={count} total={total} />
          ))}
        </div>
      )}

      {/* All codes table */}
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider w-20">NIBRS</th>
              <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">Offense</th>
              <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">Description</th>
              <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider w-24">Category</th>
              <th className="text-right px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider w-20">Count</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50 transition">
                <td className="px-3 py-2 text-xs font-mono text-gray-600">{row.code}</td>
                <td className="px-3 py-2 text-sm text-navy">{row.offense}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{row.description}</td>
                <td className="px-3 py-2 text-xs">
                  <CategoryPill category={row.crime_against} />
                </td>
                <td className="px-3 py-2 text-right font-bold text-sm text-navy">{row.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hiddenCount > 0 && !showAll && (
        <button
          onClick={() => setShowAll(true)}
          className="text-sm text-teal hover:text-teal-dark font-semibold mt-3"
        >
          Show {hiddenCount} more code{hiddenCount > 1 ? 's' : ''}
        </button>
      )}
      {showAll && rows.length > 8 && (
        <button
          onClick={() => setShowAll(false)}
          className="text-sm text-gray-400 hover:text-gray-600 mt-3"
        >
          Show less
        </button>
      )}
    </div>
  );
}

function CategoryBadge({ category, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const colors = categoryColors(category);

  return (
    <div className={`flex-1 rounded-lg border px-3 py-2 ${colors.bg} ${colors.border}`}>
      <div className={`text-xs font-semibold uppercase tracking-wider ${colors.text}`}>
        {category}
      </div>
      <div className="flex items-baseline gap-2 mt-0.5">
        <span className={`text-lg font-bold ${colors.text}`}>{count}</span>
        <span className="text-xs text-gray-500">({pct}%)</span>
      </div>
    </div>
  );
}

function CategoryPill({ category }) {
  const colors = categoryColors(category);
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold border ${colors.bg} ${colors.text} ${colors.border}`}>
      {category || 'Unknown'}
    </span>
  );
}

function categoryColors(category) {
  switch (category) {
    case 'Person':
      return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
    case 'Property':
      return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
    case 'Society':
      return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' };
    default:
      return { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' };
  }
}
